import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, firebaseConfig } from '../firebaseConfig';
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  getDoc,
  writeBatch,
  deleteDoc,
  arrayUnion
} from 'firebase/firestore';
import type { Bus } from '../App';
import { useTranslation } from 'react-i18next';
import { getFunctions, httpsCallable } from "firebase/functions";

interface StudentManagementProps {
  schoolId: string;
  mode: 'form' | 'list';
  schoolName?: string;
  schoolTechnicalId?: string;
  onCancel?: () => void;
}

const StudentManagement: React.FC<StudentManagementProps> = ({
  schoolId,
  mode: initialMode,
  schoolName,
  schoolTechnicalId,
  onCancel
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [buses, setBuses] = useState<Bus[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [displaySchoolId, setDisplaySchoolId] = useState<string>('');
  const [isSendingBulk, setIsSendingBulk] = useState(false);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'form' | 'list'>(initialMode);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [busFilter, setBusFilter] = useState('');
  const [currentMomentFilter, setCurrentMomentFilter] = useState<'matin' | 'apres-midi' | null>(null);

  const [parentExists, setParentExists] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTargetBus, setTransferTargetBus] = useState<string>('');
  const [isTransferring, setIsTransferring] = useState(false);

  // ✅ NOUVEAU : état du géocodage
  const [geocodingStatus, setGeocodingStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const days = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
  const moments = [
    { key: 'morningTrip', label: t('mom_morning'), tag: 'morningtrip' },
    { key: 'noonTrip', label: t('mom_noon'), tag: 'noontrip' },
    { key: 'afternoonTrip', label: t('mom_afternoon'), tag: 'afternoontrip' },
    { key: 'eveningTrip', label: t('mom_evening'), tag: 'eveningtrip' }
  ];

  const initialFormState = {
    name: '',
    studentClass: '',
    parentEmail: '',
    parentPhone: '',
    address: '',
    routeDetails: '',
    assignedBusNumber: '',
    lundi: false, mardi: false, mercredi: false, jeudi: false, vendredi: false, samedi: false, dimanche: false,
    morningTrip: false, noonTrip: false, afternoonTrip: false, eveningTrip: false,
    password: '',
    credentialsSent: false,
    isPresent: false,
    schoolId: schoolId,
    homeLatitude: 0.0,
    homeLongitude: 0.0,
    searchTags: []
  };

  const [formData, setFormData] = useState<any>(initialFormState);

  const generateSearchTags = (data: any) => {
    const tags: string[] = [];
    if (!data.assignedBusNumber) return [];
    tags.push(`BUS_${data.assignedBusNumber}`);
    days.forEach(day => {
      if (data[day] === true) {
        moments.forEach(m => {
          if (data[m.key] === true) {
            tags.push(`BUS_${data.assignedBusNumber}_${day}_${m.tag}`);
          }
        });
      }
    });
    return tags;
  };


  // ✅ GÉOCODAGE avec GOOGLE MAPS API (identique au package Flutter 'geocoding')
const getCoordinates = async (address: string): Promise<{ lat: number; lon: number; success: boolean }> => {
  if (!address || address.trim().length < 5) return { lat: 0, lon: 0, success: false };
  
  try {
    // Nettoyer l'adresse
    let cleanAddress = address.replace(/(\d)([a-zA-Z])/g, '$1 $2').trim();
    
    console.log('🔍 Géocodage Google de:', cleanAddress);
    
    // ✅ Google Geocoding API
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(cleanAddress)}&key=${firebaseConfig.apiKey}&language=fr&region=ma`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      console.error(`Google Geocoding HTTP ${response.status}`);
      return { lat: 0, lon: 0, success: false };
    }
    
    const data = await response.json();
    
    if (data.status === 'OK' && data.results && data.results.length > 0) {
      const location = data.results[0].geometry.location;
      const lat = location.lat;
      const lon = location.lng;
      
      if (!isNaN(lat) && !isNaN(lon) && lat !== 0 && lon !== 0) {
        console.log(`✅ Google Geocoding OK: ${lat}, ${lon}`);
        console.log(`📍 Adresse formatée: ${data.results[0].formatted_address}`);
        return { lat, lon, success: true };
      }
    } else if (data.status === 'ZERO_RESULTS') {
      console.warn('❌ Google Geocoding: ZERO_RESULTS');
    } else if (data.status === 'REQUEST_DENIED') {
      console.error('❌ Google Geocoding: REQUEST_DENIED -', data.error_message);
      console.error('⚠️ Active Geocoding API : https://console.cloud.google.com/apis/library/geocoding-backend.googleapis.com');
    } else {
      console.warn('❌ Google Geocoding status:', data.status, data.error_message || '');
    }
  } catch (error) {
    console.error("Erreur géocodage Google:", error);
  }
  
  return { lat: 0, lon: 0, success: false };
};

  // ✅ Géocoder l'adresse du formulaire (appelé par le bouton 📍)
  const geocodeFormAddress = async () => {
    if (!formData.address || formData.address.trim().length < 5) {
      alert(t('err_address_required', 'Veuillez saisir une adresse valide (min. 5 caractères).'));
      return;
    }
    
    setGeocodingStatus('loading');
    
    try {
      const result = await getCoordinates(formData.address);
      
      if (result.success) {
        setFormData((prev: any) => ({
          ...prev,
          homeLatitude: result.lat,
          homeLongitude: result.lon,
        }));
        setGeocodingStatus('success');
      } else {
        setFormData((prev: any) => ({
          ...prev,
          homeLatitude: 0,
          homeLongitude: 0,
        }));
        setGeocodingStatus('error');
        alert(t('err_address_not_found', '❌ Adresse introuvable. Vérifiez l\'orthographe ou saisissez les coordonnées manuellement.'));
      }
    } catch (error) {
      setGeocodingStatus('error');
      console.error('Erreur géocodage:', error);
    } finally {
      setTimeout(() => setGeocodingStatus('idle'), 3000);
    }
  };

  const getSchoolMetierId = async (): Promise<string> => {
    try {
      const schoolSnap = await getDoc(doc(db, "schools", schoolId));
      if (schoolSnap.exists()) {
        const data = schoolSnap.data();
        return data.schoolId || data.schoolTechnicalId || schoolId;
      }
    } catch (error) { console.error('Erreur schoolId métier:', error); }
    return schoolId;
  };

  useEffect(() => {
    setViewMode(initialMode);
    if (initialMode === 'form') { setEditingId(null); setFormData(initialFormState); }
  }, [initialMode, schoolId]);

  useEffect(() => {
    const checkParent = async () => {
      const email = formData.parentEmail?.trim().toLowerCase();
      if (email && email.includes('@') && email.includes('.')) {
        const parentRef = doc(db, "schools", schoolId, "parents", email);
        const parentSnap = await getDoc(parentRef);
        if (parentSnap.exists()) {
          setParentExists(true);
          const pData = parentSnap.data();
          setFormData((prev: any) => ({ 
            ...prev, 
            password: pData.password,
            homeLatitude: prev.homeLatitude || pData.homeLatitude || 0,
            homeLongitude: prev.homeLongitude || pData.homeLongitude || 0
          }));
        } else { setParentExists(false); }
      } else { setParentExists(false); }
    };
    checkParent();
  }, [formData.parentEmail, schoolId]);

  useEffect(() => {
    const fetchSchoolData = async () => {
      const schoolRef = doc(db, "schools", schoolId);
      const schoolSnap = await getDoc(schoolRef);
      if (schoolSnap.exists()) setDisplaySchoolId(schoolSnap.data().schoolId || schoolId);
    };
    fetchSchoolData();
    const unsubBuses = onSnapshot(collection(db, "schools", schoolId, "buses"), (snap) => {
      setBuses(snap.docs.map(d => ({ id: d.id, ...d.data() } as Bus)));
    });
    const unsubStudents = onSnapshot(collection(db, "schools", schoolId, "students"), (snap) => {
      setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => { unsubBuses(); unsubStudents(); };
  }, [schoolId]);

  const filteredStudents = useMemo(() => {
    return students.filter(s => {
      const matchesSearch = (s.name || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesClass = (classFilter === '' || s.studentClass === classFilter);
      const matchesBus = (busFilter === '' || s.assignedBusNumber === busFilter);
      let matchesMoment = true;
      if (currentMomentFilter === 'matin') matchesMoment = s.morningTrip === true || s.noonTrip === true;
      else if (currentMomentFilter === 'apres-midi') matchesMoment = s.afternoonTrip === true || s.eveningTrip === true;
      return matchesSearch && matchesClass && matchesBus && matchesMoment;
    }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [students, searchTerm, classFilter, busFilter, currentMomentFilter]);

  const availableClasses = useMemo(() => Array.from(new Set(students.map(s => s.studentClass).filter(Boolean))).sort(), [students]);

  // ✅ IMPORT CSV
  const handleCSVImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      if (!text) return alert("Erreur de lecture du fichier.");
      const lines = text.split('\n').filter(line => line.trim() !== '');
      if (lines.length <= 1) return alert("Le fichier est vide ou ne contient que l'en-tête.");
      
      const separator = lines[0].includes(';') ? ';' : ',';
      const headers = lines[0].toLowerCase().split(separator).map(h => h.trim().replace(/^"|"$/g, ''));
      
      const nameIndex = headers.findIndex(h => h.includes('name') || h.includes('nom') || h.includes('nombre') || h.includes('الاسم'));
      const classIndex = headers.findIndex(h => h.includes('class') || h.includes('classe') || h.includes('curso') || h.includes('فصل'));
      const emailIndex = headers.findIndex(h => h.includes('email') || h.includes('correo') || h.includes('بريد'));
      const addressIndex = headers.findIndex(h => h.includes('address') || h.includes('adresse') || h.includes('direccion') || h.includes('عنوان'));
      const busIndex = headers.findIndex(h => h.includes('bus') || h.includes('autobus') || h.includes('حافلة'));
      const morningIndex = headers.findIndex(h => h.includes('morning') || h.includes('matin') || h.includes('mañana') || h.includes('صباح'));
      const noonIndex = headers.findIndex(h => h.includes('noon') || h.includes('midi') || h.includes('mediodía') || h.includes('ظهر'));
      const afternoonIndex = headers.findIndex(h => h.includes('afternoon') || h.includes('après-midi') || h.includes('tarde') || h.includes('بعد الظهر'));
      const eveningIndex = headers.findIndex(h => h.includes('evening') || h.includes('soir') || h.includes('noche') || h.includes('مساء'));
      const mondayIndex = headers.findIndex(h => h.includes('monday') || h.includes('lundi') || h.includes('lunes') || h.includes('الاثنين'));
      const tuesdayIndex = headers.findIndex(h => h.includes('tuesday') || h.includes('mardi') || h.includes('martes') || h.includes('الثلاثاء'));
      const wednesdayIndex = headers.findIndex(h => h.includes('wednesday') || h.includes('mercredi') || h.includes('miércoles') || h.includes('الأربعاء'));
      const thursdayIndex = headers.findIndex(h => h.includes('thursday') || h.includes('jeudi') || h.includes('jueves') || h.includes('الخميس'));
      const fridayIndex = headers.findIndex(h => h.includes('friday') || h.includes('vendredi') || h.includes('viernes') || h.includes('الجمعة'));
      const saturdayIndex = headers.findIndex(h => h.includes('saturday') || h.includes('samedi') || h.includes('sábado') || h.includes('السبت'));
      const sundayIndex = headers.findIndex(h => h.includes('sunday') || h.includes('dimanche') || h.includes('domingo') || h.includes('الأحد'));
      
      const schoolMetierId = await getSchoolMetierId();
      const batch = writeBatch(db);
      let successCount = 0;
      let errorCount = 0;
      
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const columns = lines[i].split(separator).map(v => v?.trim().replace(/^"|"$/g, ''));
        
        const name = nameIndex !== -1 ? columns[nameIndex] : columns[0];
        const studentClass = classIndex !== -1 ? columns[classIndex] : columns[1];
        const parentEmail = emailIndex !== -1 ? columns[emailIndex] : columns[2];
        const address = addressIndex !== -1 ? columns[addressIndex] : columns[3];
        const assignedBusNumber = busIndex !== -1 ? columns[busIndex] : columns[4];
        
        const morningTrip = morningIndex !== -1 ? (columns[morningIndex] === 'true' || columns[morningIndex] === '1') : true;
        const noonTrip = noonIndex !== -1 ? (columns[noonIndex] === 'true' || columns[noonIndex] === '1') : false;
        const afternoonTrip = afternoonIndex !== -1 ? (columns[afternoonIndex] === 'true' || columns[afternoonIndex] === '1') : true;
        const eveningTrip = eveningIndex !== -1 ? (columns[eveningIndex] === 'true' || columns[eveningIndex] === '1') : false;
        
        const monday = mondayIndex !== -1 ? (columns[mondayIndex] === 'true' || columns[mondayIndex] === '1') : true;
        const tuesday = tuesdayIndex !== -1 ? (columns[tuesdayIndex] === 'true' || columns[tuesdayIndex] === '1') : true;
        const wednesday = wednesdayIndex !== -1 ? (columns[wednesdayIndex] === 'true' || columns[wednesdayIndex] === '1') : true;
        const thursday = thursdayIndex !== -1 ? (columns[thursdayIndex] === 'true' || columns[thursdayIndex] === '1') : true;
        const friday = fridayIndex !== -1 ? (columns[fridayIndex] === 'true' || columns[fridayIndex] === '1') : true;
        const saturday = saturdayIndex !== -1 ? (columns[saturdayIndex] === 'true' || columns[saturdayIndex] === '1') : false;
        const sunday = sundayIndex !== -1 ? (columns[sundayIndex] === 'true' || columns[sundayIndex] === '1') : false;
        
        if (!name || !parentEmail) { errorCount++; continue; }
        
        const cleanEmail = parentEmail.trim().toLowerCase().replace(/\.@/g, '@').replace(/@+/g, '@');
        const studentRef = doc(collection(db, "schools", schoolId, "students"));
        const parentRef = doc(db, "schools", schoolId, "parents", cleanEmail);
        let finalPassword = Math.floor(100000 + Math.random() * 900000).toString();
        
        // ✅ Géocodage automatique pendant l'import
        let finalLat = 0;
        let finalLon = 0;
        if (address && address.length > 5) {
          const coords = await getCoordinates(address);
          if (coords.success) {
            finalLat = coords.lat;
            finalLon = coords.lon;
          }
        }
        
        const parentSnap = await getDoc(parentRef);
        if (parentSnap.exists()) {
          batch.update(parentRef, { studentNames: arrayUnion(name) });
          finalPassword = parentSnap.data().password; 
        } else {
          batch.set(parentRef, {
            parentEmail: cleanEmail, 
            password: finalPassword, 
            studentNames: [name],
            schoolId: schoolMetierId,
            address: address || '', 
            homeLatitude: finalLat,
            homeLongitude: finalLon,
            // Statut PRO
            isPro: false,
            proActivatedAt: null,
            proExpiredAt: null,
            proActivatedBySchool: false,
            // Préférences
            locale: i18n.language || 'fr',
            pref_notif_demarrage: true,
            pref_notif_presence: true,
            pref_notif_proximite: true,
            pref_show_pins: true,
            pref_show_dist_time: true,
            // Notifications
            soundEnabled: true,
            fcmToken: null,
          });
        }
        
        const studentData = {
          name,
          studentClass: studentClass || '',
          parentEmail: cleanEmail,
          parentPhone: '',
          address: address || '',
          assignedBusNumber: assignedBusNumber?.toString() || '',
          morningTrip, noonTrip, afternoonTrip, eveningTrip,
          lundi: monday, mardi: tuesday, mercredi: wednesday, jeudi: thursday,
          vendredi: friday, samedi: saturday, dimanche: sunday,
          password: finalPassword,
          schoolId: schoolMetierId,
          isPresent: false,
          credentialsSent: false,
          homeLatitude: finalLat,
          homeLongitude: finalLon,
          morningIsPresent: false,
          noonIsPresent: false,
          afternoonIsPresent: false,
          eveningIsPresent: false,
          searchTags: generateSearchTags({
            assignedBusNumber: assignedBusNumber?.toString() || '',
            lundi: monday, mardi: tuesday, mercredi: wednesday, jeudi: thursday,
            vendredi: friday, samedi: saturday, dimanche: sunday,
            morningTrip, noonTrip, afternoonTrip, eveningTrip
          })
        };
        
        batch.set(studentRef, studentData);
        successCount++;
      }
      
      try { 
        await batch.commit(); 
        alert(`${successCount} élèves importés avec succès !`);
      } catch (e) { 
        console.error(e);
        alert("Erreur lors de l'import."); 
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  // ✅ HANDLE SAVE avec géocodage automatique
  const handleSave = async () => {
    if (!formData.name || !formData.parentEmail || !formData.address) return alert(t('err_name_email_required'));
    const batch = writeBatch(db);
    const cleanEmail = formData.parentEmail.trim().toLowerCase();
    const studentRef = editingId ? doc(db, "schools", schoolId, "students", editingId) : doc(collection(db, "schools", schoolId, "students"));
    const parentRef = doc(db, "schools", schoolId, "parents", cleanEmail);
    
    try {
      let lat = parseFloat(formData.homeLatitude) || 0;
      let lon = parseFloat(formData.homeLongitude) || 0;

      // ✅ Géocodage automatique si 0
      if (lat === 0 && lon === 0 && formData.address && formData.address.length > 5) {
        console.log('🔍 Géocodage automatique de:', formData.address);
        setGeocodingStatus('loading');
        
        const coords = await getCoordinates(formData.address);
        
        if (coords.success) {
          lat = coords.lat;
          lon = coords.lon;
          setGeocodingStatus('success');
          console.log(`✅ Coordonnées trouvées: ${lat}, ${lon}`);
        } else {
          setGeocodingStatus('error');
          const proceed = window.confirm(
            t('confirm_save_without_gps', 
              '⚠️ Adresse introuvable. Voulez-vous enregistrer SANS coordonnées GPS ?\n\n(L\'app ne pourra pas calculer la distance pour cet élève.)')
          );
          if (!proceed) {
            setGeocodingStatus('idle');
            return;
          }
        }
      }
      
      const parentSnap = await getDoc(parentRef);
      let finalPassword = formData.password;
      if (parentSnap.exists()) {
        finalPassword = parentSnap.data().password; 
        if (lat === 0 && lon === 0) { 
          lat = parentSnap.data().homeLatitude || 0; 
          lon = parentSnap.data().homeLongitude || 0; 
        }
      } else if (!finalPassword) {
        finalPassword = Math.floor(100000 + Math.random() * 900000).toString();
      }

      const tags = generateSearchTags(formData);
      const schoolMetierId = await getSchoolMetierId();
      const { latitude: _lat, longitude: _lon, ...formDataClean } = formData as any;

      const studentFinalData = { 
        ...formDataClean,
        id: studentRef.id, 
        parentEmail: cleanEmail, 
        password: finalPassword, 
        schoolId: schoolMetierId,
        homeLatitude: lat,
        homeLongitude: lon,
        searchTags: tags,
        isPresent: editingId ? formData.isPresent : false,
        morningIsPresent: formData.morningIsPresent || false,
        noonIsPresent: formData.noonIsPresent || false,
        afternoonIsPresent: formData.afternoonIsPresent || false,
        eveningIsPresent: formData.eveningIsPresent || false,
      };

      batch.set(studentRef, studentFinalData, { merge: true });
      
      if (parentSnap.exists()) { 
        batch.update(parentRef, { 
          studentNames: arrayUnion(formData.name),
          address: formData.address,
          homeLatitude: lat,
          homeLongitude: lon
        }); 
      } else { 
        batch.set(parentRef, { 
          parentEmail: cleanEmail, 
          password: finalPassword, 
          studentNames: [formData.name], 
          schoolId: schoolMetierId,
          address: formData.address,
          homeLatitude: lat,
          homeLongitude: lon,
          // Statut PRO
          isPro: false,
          proActivatedAt: null,
          proExpiredAt: null,
          proActivatedBySchool: false,
          // Préférences
          locale: i18n.language || 'fr',
          pref_notif_demarrage: true,
          pref_notif_presence: true,
          pref_notif_proximite: true,
          pref_show_pins: true,
          pref_show_dist_time: true,
          // Notifications
          soundEnabled: true,
          fcmToken: null,
        }); 
      }
      
      await batch.commit();
      setViewMode('list'); setFormData(initialFormState); setEditingId(null);
      setGeocodingStatus('idle');
    } catch (e) { 
      console.error(e);
      alert(t('err_save')); 
    }
  };

  const sendEmailTemplate = async (student: any) => {
    if (!student?.parentEmail || !student?.password) return;
    const currentSchoolName = schoolName || "Votre École";
    const currentSchoolId = schoolTechnicalId || displaySchoolId;
    const mailDir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    const textAlign = i18n.language === 'ar' ? 'right' : 'left';
    
    const emailHtml = `
<div dir="${mailDir}" style="background-color: #ffffff; color: #000000; font-family: Arial, sans-serif; padding: 30px 20px; max-width: 600px; margin: auto; border: 1px solid #eeeeee; border-radius: 8px; text-align: ${textAlign};">
<div style="text-align: center; margin-bottom: 25px;">
<h1 style="color: #000000; margin: 0; font-size: 24px; text-transform: uppercase; font-weight: bold;">${currentSchoolName}</h1>
<hr style="border: none; border-top: 1px solid #eeeeee; margin: 20px auto; width: 100%;">
</div>
<div style="padding: 0 5px;">
<p style="font-size: 16px; margin-bottom: 20px;">${t('mail_greeting')}</p>
<p style="font-size: 15px; line-height: 1.6; color: #000000; margin-bottom: 25px;">
${t('mail_welcome', { name: student.name })}
</p>
<div style="margin-bottom: 30px;">
<p style="font-size: 16px; font-weight: bold; margin-bottom: 15px;">${t('mail_info_title')}</p>
<p style="margin: 8px 0; font-size: 15px;"><strong>${t('mail_school_id')} :</strong> ${currentSchoolId}</p>
<p style="margin: 8px 0; font-size: 15px;"><strong>${t('mail_email_label')} :</strong> ${student.parentEmail}</p>
<p style="margin: 8px 0; font-size: 15px;"><strong>${t('mail_password_label')} :</strong> ${student.password}</p>
</div>
<p style="font-size: 14px; color: #333333; margin-bottom: 25px;">
${t('mail_keep_safe')}
</p>
<hr style="border: none; border-top: 1px solid #eeeeee; margin: 20px 0;">
<p style="font-size: 12px; line-height: 1.5; color: #666666; margin-bottom: 25px;">
${t('mail_security_note')}
</p>
<p style="font-size: 15px; font-weight: bold; margin: 0;">${t('mail_team')}</p>
</div>
</div>`;

    try {
      // ✅ APPEL DE LA CLOUD FUNCTION AU LIEU DE L'ÉCRITURE DIRECTE
      const functions = getFunctions();
      const sendEmailFn = httpsCallable(functions, 'sendEmail');
      
      await sendEmailFn({
        to: student.parentEmail,
        subject: `${currentSchoolName} - ${t('mail_subject')}`,
        html: emailHtml,
        senderType: 'admin_react' // 👈 Permet de passer sans authentification
      });

      // Mettre à jour le statut "credentialsSent"
      if (student.id) {
        await updateDoc(doc(db, "schools", schoolId, "students", student.id), { credentialsSent: true });
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email :", error);
      alert("Erreur d'envoi d'email.");
    }
  };

  const handleSendFromForm = async () => {
    const cleanEmail = formData.parentEmail.trim().toLowerCase();
    const parentRef = doc(db, "schools", schoolId, "parents", cleanEmail);
    const parentSnap = await getDoc(parentRef);
    const passwordToSend = parentSnap.exists() ? parentSnap.data().password : (formData.password || Math.floor(100000 + Math.random() * 900000).toString());
    try {
      await sendEmailTemplate({ ...formData, password: passwordToSend });
      if (editingId) await updateDoc(doc(db, "schools", schoolId, "students", editingId), { credentialsSent: true, password: passwordToSend });
      setFormData((prev: any) => ({ ...prev, credentialsSent: true, password: passwordToSend }));
      alert(t('alert_send_success'));
    } catch (error) { alert(t('err_send')); }
  };
  
  const handleSmartSend = async () => {
    const studentsToProcess = selectedIds.length > 0 ? students.filter(s => selectedIds.includes(s.id) && s.parentEmail) : filteredStudents.filter(s => !s.credentialsSent && s.parentEmail);
    if (studentsToProcess.length === 0) return alert(t('err_no_students_pending'));
    if (!window.confirm(t('conf_send_all', { count: studentsToProcess.length }))) return;
    setIsSendingBulk(true);
    try {
      const uniqueEmails = Array.from(new Set(studentsToProcess.map(s => s.parentEmail)));
      for (const email of uniqueEmails) {
        const student = studentsToProcess.find(s => s.parentEmail === email);
        if (student) await sendEmailTemplate(student);
      }
      alert(t('alert_send_success'));
      setSelectedIds([]);
    } catch (e) { alert(t('err_send')); }
    setIsSendingBulk(false);
  };

  const toggleSelect = (id: string) => setSelectedIds((prev: string[]) => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(selectedIds.length === filteredStudents.length ? [] : filteredStudents.map(s => s.id));

  const handleDeleteBulk = async () => {
    if (selectedIds.length === 0) return alert(t('err_select_delete'));
    if (window.confirm(t('conf_delete_bulk', { count: selectedIds.length }))) {
      const batch = writeBatch(db);
      selectedIds.forEach(id => batch.delete(doc(db, "schools", schoolId, "students", id)));
      await batch.commit(); setSelectedIds([]);
    }
  };

  const handleBulkTransfer = async () => {
    if (selectedIds.length === 0) {
      return alert(t('err_select_transfer', 'Veuillez sélectionner au moins un élève.'));
    }
    if (!transferTargetBus) {
      return alert(t('err_choose_bus', 'Veuillez choisir un bus de destination.'));
    }

    const targetBus = buses.find(b => String(b.number) === String(transferTargetBus));
    if (!targetBus) return alert(t('err_bus_not_found', 'Bus introuvable.'));

    // Vérification de capacité
    const currentStudentsInTarget = students.filter(
      s => String(s.assignedBusNumber) === String(transferTargetBus) && !selectedIds.includes(s.id)
    ).length;
    const capacity = Number(targetBus.capacity) || 0;
    
    if (capacity > 0 && (currentStudentsInTarget + selectedIds.length) > capacity) {
      const confirmOver = window.confirm(
        t('conf_capacity_exceeded', {
          bus: transferTargetBus,
          current: currentStudentsInTarget + selectedIds.length,
          capacity: capacity,
          defaultValue: `⚠️ Capacité du bus ${transferTargetBus} dépassée (${currentStudentsInTarget + selectedIds.length}/${capacity}). Continuer quand même ?`
        })
      );
      if (!confirmOver) return;
    }

    // Confirmation du transfert
    const confirmTransfer = window.confirm(
      t('conf_bulk_transfer', {
        count: selectedIds.length,
        bus: transferTargetBus,
        defaultValue: `Transférer ${selectedIds.length} élève(s) vers le bus ${transferTargetBus} ?`
      })
    );
    if (!confirmTransfer) return;

    setIsTransferring(true);
    try {
      const batch = writeBatch(db);

      for (const studentId of selectedIds) {
        const student = students.find(s => s.id === studentId);
        if (!student) continue;

        const newSearchTags = generateSearchTags({
          ...student,
          assignedBusNumber: String(transferTargetBus),
        });

        batch.update(doc(db, "schools", schoolId, "students", studentId), {
          assignedBusNumber: String(transferTargetBus),
          searchTags: newSearchTags,
          updatedAt: new Date(),
        });
      }

      await batch.commit();

      // Message de succès
      alert(t('alert_transfer_success', {
        count: selectedIds.length,
        bus: transferTargetBus,
        defaultValue: `${selectedIds.length} élève(s) transféré(s) vers le bus ${transferTargetBus}.`
      }));
      
      setSelectedIds([]);
      setShowTransferModal(false);
      setTransferTargetBus('');
    } catch (error) {
      console.error(error);
      alert(t('err_transfer', 'Erreur lors du transfert.'));
    } finally {
      setIsTransferring(false);
    }
  };

  // ✅ INDICATEUR : coordonnées présentes ?
  const hasValidCoords = 
    formData.homeLatitude && formData.homeLongitude && 
    formData.homeLatitude !== 0 && formData.homeLongitude !== 0;

  return (
    <div className="student-management-wrapper" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      {viewMode === 'form' && (
        <div className="student-form-container" style={{ paddingTop: '20px' }}>
          <div className="form-grid-main">
            <div className="form-column">
              <div className="section-title">👤 {t('sec_id')}</div>
              <div className="input-with-icon"><span className="input-icon-field">👤</span><input className="input-styled-icon" placeholder={t('p_full_name') + " *"} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} /></div>
              <div className="input-with-icon"><span className="input-icon-field">🏫</span><input className="input-styled-icon" placeholder={t('p_class')} value={formData.studentClass} onChange={e => setFormData({ ...formData, studentClass: e.target.value })} /></div>
              <div className="section-title">📍 {t('sec_contact')}</div>
              <div className="input-with-icon">
                <span className="input-icon-field">✉️</span>
                <input className="input-styled-icon" type="email" placeholder={t('p_parent_email') + " *"} value={formData.parentEmail} onChange={e => setFormData({ ...formData, parentEmail: e.target.value })} />
              </div>
              {parentExists && (
                <div style={{ fontSize: '0.75rem', color: '#2b6cb0', background: '#ebf8ff', padding: '5px 10px', borderRadius: '5px', marginTop: '-10px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', border: '1px solid #bee3f8' }}>
                  ℹ️ {t('parent_found_msg', 'Famille déjà enregistrée : mot de passe récupéré automatiquement.')}
                </div>
              )}
              <div className="input-with-icon"><span className="input-icon-field">📞</span><input className="input-styled-icon" placeholder={t('p_parent_phone')} value={formData.parentPhone} onChange={e => setFormData({ ...formData, parentPhone: e.target.value })} /></div>
              
              {/* ✅ Adresse + Bouton Localiser */}
              <div className="input-with-icon" style={{ alignItems: 'flex-start' }}>
                <span className="input-icon-field" style={{ marginTop: '10px' }}>🏠</span>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <textarea 
                    className="input-styled-icon" 
                    placeholder={t('p_address') + " *"} 
                    value={formData.address} 
                    onChange={e => setFormData({ ...formData, address: e.target.value })} 
                    style={{ minHeight: '80px', paddingTop: '10px', width: '100%', boxSizing: 'border-box' }} 
                  />
                  <button
                    type="button"
                    onClick={geocodeFormAddress}
                    disabled={geocodingStatus === 'loading' || !formData.address || formData.address.length < 5}
                    style={{
                      padding: '8px 16px',
                      background: geocodingStatus === 'success' ? '#10b981' : 
                                 geocodingStatus === 'error' ? '#ef4444' : 
                                 geocodingStatus === 'loading' ? '#94a3b8' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      cursor: geocodingStatus === 'loading' ? 'wait' : 'pointer',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.2s',
                      opacity: (!formData.address || formData.address.length < 5) ? 0.5 : 1,
                    }}
                  >
                    {geocodingStatus === 'loading' ? `⏳ ${t('geocoding_loading', 'Recherche en cours...')}` : 
                     geocodingStatus === 'success' ? `✅ ${t('geocoding_success', 'Adresse localisée !')}` : 
                     geocodingStatus === 'error' ? `❌ ${t('geocoding_error', 'Adresse introuvable')}` : 
                     `📍 ${t('geocode_address', 'Localiser cette adresse')}`}
                  </button>
                </div>
              </div>

              {/* ✅ Indicateur visuel GPS */}
              <div style={{ 
                fontSize: '0.75rem', 
                color: hasValidCoords ? '#10b981' : '#ef4444',
                fontWeight: 'bold',
                marginTop: '10px',
                marginBottom: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                {hasValidCoords ? 
                  `✅ ${t('gps_coordinates_ok', 'Coordonnées GPS renseignées')}` : 
                  `⚠️ ${t('gps_coordinates_missing', 'Coordonnées GPS non renseignées')}`}
              </div>

              {/* Champs GPS */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <div className="input-with-icon" style={{ flex: 1 }}>
                  <span className="input-icon-field">🌐</span>
                  <input type="number" step="any" className="input-styled-icon" placeholder="Latitude" value={formData.homeLatitude || ''} onChange={e => { const val = parseFloat(e.target.value) || 0; setFormData({ ...formData, homeLatitude: val }); }} />
                </div>
                <div className="input-with-icon" style={{ flex: 1 }}>
                  <span className="input-icon-field">🌐</span>
                  <input type="number" step="any" className="input-styled-icon" placeholder="Longitude" value={formData.homeLongitude || ''} onChange={e => { const val = parseFloat(e.target.value) || 0; setFormData({ ...formData, homeLongitude: val }); }} />
                </div>
              </div>
            </div>

            <div className="form-column">
              <div className="section-title">🚌 {t('sec_bus')}</div>
              <div className="input-with-icon">
                <select className="input-styled-icon" value={formData.assignedBusNumber} onChange={e => setFormData({ ...formData, assignedBusNumber: e.target.value })}>
                  <option value="">-- {t('p_choose_bus')} --</option>
                  {buses.map(b => (<option key={b.id} value={b.number}>{t('bus_label')} {b.number} {b.routeDescription ? `(${b.routeDescription})` : ''}</option>))}
                </select>
              </div>
              <div className="section-title">📅 {t('sec_days')}</div>
              <div className="compact-grid">{days.map(d => (<div key={d} className={`selectable-tag ${formData[d] ? 'active' : ''}`} onClick={() => setFormData({ ...formData, [d]: !formData[d] })}>{t(`day_${d}`)}</div>))}</div>
              <div className="section-title">⏰ {t('sec_moments')}</div>
              <div className="compact-grid">{moments.map(m => (<div key={m.key} className={`selectable-tag ${formData[m.key] ? 'active' : ''}`} onClick={() => setFormData({ ...formData, [m.key]: !formData[m.key] })}>{m.label}</div>))}</div>
            </div>

            <div className="button-group-footer" style={{ display: 'flex', gap: '20px', marginTop: '30px', gridColumn: '1 / -1' }}>
              <button type="button" onClick={handleSendFromForm} style={{ flex: 1, padding: '10px', background: '#FFC107', color: 'black', fontWeight: 800, border: 'none', borderRadius: '30px', cursor: 'pointer' }}>📧 {t('btn_send_codes')}</button>
              <button onClick={handleSave} style={{ flex: 1, padding: '10px', background: '#28a745', color: 'white', fontWeight: 800, border: 'none', borderRadius: '30px', cursor: 'pointer' }}>{editingId ? t('btn_save') : t('btn_validate')}</button>
              <button type="button" onClick={() => { setViewMode('list'); if (onCancel) onCancel(); }} style={{ flex: 1, padding: '10px', background: '#dc3545', color: 'white', fontWeight: 800, border: 'none', borderRadius: '30px', cursor: 'pointer' }}>{t('btn_cancel') || 'Annuler'}</button>
            </div>
          </div>
        </div>
      )}
      {viewMode === 'list' && (
        <div className="student-form-container" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 900 }}>{t('list_title')} ({filteredStudents.length})</h2>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input type="file" ref={fileInputRef} onChange={handleCSVImport} accept=".csv" style={{ display: 'none' }} />
              <button 
                onClick={() => setShowTransferModal(true)} 
                disabled={selectedIds.length === 0}
                style={{ 
                  padding: '8px 15px', 
                  background: selectedIds.length === 0 ? '#cbd5e0' : '#805ad5', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  fontWeight: 'bold', 
                  cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer' 
                }}
              >
                🔄 {t('btn_transfer_bus', 'Changer de bus')} ({selectedIds.length})
              </button>
              <button onClick={() => fileInputRef.current?.click()} style={{ padding: '8px 15px', background: '#4a5568', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>📥 Import CSV</button>
              <button onClick={handleDeleteBulk} style={{ padding: '8px 15px', background: '#e53e3e', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>🗑️ {t('btn_delete')} ({selectedIds.length})</button>
              <button onClick={handleSmartSend} disabled={isSendingBulk} style={{ padding: '8px 15px', background: '#28a745', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>{isSendingBulk ? t('btn_sending') : selectedIds.length > 0 ? t('btn_send_selection', { count: selectedIds.length }) : t('btn_send_all', { count: filteredStudents.filter(s => !s.credentialsSent).length })}</button>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '25px' }}>
            <button onClick={() => setCurrentMomentFilter(currentMomentFilter === 'matin' ? null : 'matin')} style={{ padding: '12px 30px', borderRadius: '30px', fontWeight: 'bold', cursor: 'pointer', backgroundColor: currentMomentFilter === 'matin' ? '#ffc107' : '#f1f5f9', color: '#2d3748', border: currentMomentFilter === 'matin' ? '2px solid #2d3748' : '2px solid transparent' }}>☀️ {currentMomentFilter === 'matin' ? t('filter_morning_active') : t('filter_morning_off')}</button>
            <button onClick={() => setCurrentMomentFilter(currentMomentFilter === 'apres-midi' ? null : 'apres-midi')} style={{ padding: '12px 30px', borderRadius: '30px', fontWeight: 'bold', cursor: 'pointer', backgroundColor: currentMomentFilter === 'apres-midi' ? '#ffc107' : '#f1f5f9', color: '#2d3748', border: currentMomentFilter === 'apres-midi' ? '2px solid #2d3748' : '2px solid transparent' }}>🌙 {currentMomentFilter === 'apres-midi' ? t('filter_afternoon_active') : t('filter_afternoon_off')}</button>
          </div>
          <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', padding: '10px', background: '#f8fafc', borderRadius: '12px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <input className="input-gps" placeholder={t('p_search_name')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ flex: 2 }} />
            <select className="input-gps" value={classFilter} onChange={(e) => setClassFilter(e.target.value)} style={{ flex: 1 }}>
              <option value="">{t('all_classes')}</option>
              {availableClasses.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="input-gps" value={busFilter} onChange={(e) => setBusFilter(e.target.value)} style={{ flex: 1 }}>
              <option value="">{t('all_buses')}</option>
              {buses.map(b => <option key={b.number} value={b.number}>{t('bus_label')} {b.number} {b.routeDescription ? `(${b.routeDescription})` : ''}</option>)}
            </select>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: isRTL ? 'right' : 'left' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f0f2f5' }}>
                <th style={{ padding: '12px' }}><input type="checkbox" checked={selectedIds.length === filteredStudents.length && filteredStudents.length > 0} onChange={toggleSelectAll} /></th>
                <th style={{ padding: '12px' }}>{t('th_status')}</th>
                <th style={{ padding: '12px' }}>{t('th_student')}</th>
                <th style={{ padding: '12px' }}>{t('th_bus')}</th>
                <th style={{ padding: '12px' }}>{t('th_connection')}</th>
                <th style={{ padding: '12px' }}>{t('th_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map(s => {
                const busInfo = buses.find(b => String(b.number) === String(s.assignedBusNumber));
                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid #f0f2f5' }}>
                    <td style={{ padding: '12px' }}><input type="checkbox" checked={selectedIds.includes(s.id)} onChange={() => toggleSelect(s.id)} /></td>
                    <td style={{ padding: '12px' }}><span style={{ fontSize: '0.65rem', padding: '4px 8px', borderRadius: '10px', background: s.credentialsSent ? '#c6f6d5' : '#fed7d7', color: s.credentialsSent ? '#2f855a' : '#c53030', fontWeight: 'bold' }}>{s.credentialsSent ? t('status_sent') : t('status_pending')}</span></td>
                    <td style={{ padding: '12px' }}><strong>{s.name}</strong><br /><span style={{ fontSize: '0.7rem', color: '#718096' }}>{s.studentClass}</span></td>
                    <td style={{ padding: '12px' }}>{s.assignedBusNumber ? (<div><strong>{t('bus_label')} {s.assignedBusNumber}</strong>{busInfo?.routeDescription && (<div style={{ fontSize: '0.65rem', color: '#2b6cb0', fontStyle: 'italic' }}>📍 {busInfo.routeDescription}</div>)}{s.routeDetails && (<div style={{ fontSize: '0.65rem', color: '#718096' }}>📝 {s.routeDetails}</div>)}</div>) : '—'}</td>
                    <td style={{ padding: '12px' }}><div style={{ fontSize: '0.75rem', color: '#4a5568' }}>✉️ {s.parentEmail || '—'}<br />🔑 {s.password || '—'}</div></td>
                    <td style={{ padding: '12px' }}>
                      <button onClick={() => sendEmailTemplate(s).then(() => alert(t('alert_send_success'))).catch(() => alert(t('err_send')))} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.1rem' }}>📧</button>
                      <button onClick={() => { setFormData(s); setEditingId(s.id); setViewMode('form'); }} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.1rem' }}>✏️</button>
                      <button onClick={async () => { if (window.confirm(t('conf_delete'))) await deleteDoc(doc(db, "schools", schoolId, "students", s.id)); }} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.1rem' }}>🗑️</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ✅ MODALE DE TRANSFERT EN MASSE */}
      {showTransferModal && (
        <div 
          onClick={() => !isTransferring && setShowTransferModal(false)}
          style={{ 
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', 
            justifyContent: 'center', zIndex: 2000, padding: '20px' 
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{ 
              background: 'white', borderRadius: '16px', padding: '30px', 
              maxWidth: '450px', width: '100%', textAlign: isRTL ? 'right' : 'left',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
          >
            <div style={{ fontSize: '2.5rem', textAlign: 'center', marginBottom: '10px' }}>🔄</div>
            <h3 style={{ margin: '0 0 10px 0', textAlign: 'center', color: '#1a202c' }}>
              {t('modal_transfer_title', 'Transférer les élèves')}
            </h3>
            <p style={{ textAlign: 'center', color: '#718096', marginBottom: '20px' }}>
              {t('modal_transfer_desc', '{{count}} élève(s) sélectionné(s)', { count: selectedIds.length })}
            </p>

            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666', display: 'block', marginBottom: '6px' }}>
              {t('lbl_destination_bus', 'Bus de destination')}
            </label>
            <select
              value={transferTargetBus}
              onChange={(e) => setTransferTargetBus(e.target.value)}
              disabled={isTransferring}
              style={{ 
                width: '100%', padding: '12px', borderRadius: '10px', 
                border: '1px solid #cbd5e0', fontSize: '14px', marginBottom: '20px',
                fontWeight: 'bold', cursor: 'pointer'
              }}
            >
              <option value="">-- {t('choose_bus', 'Choisir un bus')} --</option>
              {buses.map(b => (
                <option key={b.id} value={String(b.number)}>
                  {t('bus_label')} {b.number} 
                  {b.routeDescription ? ` (${b.routeDescription})` : ''}
                  {b.capacity ? ` — ${b.capacity} ${t('places', 'places')}` : ''}
                </option>
              ))}
            </select>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { setShowTransferModal(false); setTransferTargetBus(''); }}
                disabled={isTransferring}
                style={{ 
                  flex: 1, padding: '12px', background: '#e2e8f0', color: '#475569', 
                  border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' 
                }}
              >
                ✖ {t('btn_cancel') || 'Annuler'}
              </button>
              <button
                onClick={handleBulkTransfer}
                disabled={isTransferring || !transferTargetBus}
                style={{ 
                  flex: 1, padding: '12px', 
                  background: isTransferring || !transferTargetBus ? '#a0aec0' : '#805ad5', 
                  color: 'white', border: 'none', borderRadius: '10px', 
                  fontWeight: 'bold', cursor: isTransferring || !transferTargetBus ? 'not-allowed' : 'pointer' 
                }}
              >
                {isTransferring ? '⏳...' : `🔄 ${t('btn_confirm_transfer', 'Transférer')}`}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StudentManagement;